import React, { useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { useNavigate } from "react-router-dom";

// ✅ Correct CSS imports for latest react-pdf version
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// Set up PDF.js worker from public folder
pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

const sampleBooks = [
  {
    id: 1,
    title: "Effective Communication in Palliative Care",
    author: "Palliative Care Team",
    description:
      "This book explores the crucial role of communication in palliative care, providing strategies and techniques for healthcare professionals to effectively communicate with patients and their families during end-of-life care.",
    category: "Self-Help",
    cover_image_path:
      "https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=300&h=400&fit=crop",
    pdf_file_path: "/books/communication.pdf",
    rating: 4.5,
    published_year: 2026,
    language: "Malayalam",
  },
  {
    id: 2,
    title: "Equipments",
    author: "Palliative Care Team ",
    description:
      "A comprehensive guide to essential medical equipment used in palliative care, covering their functions, applications, and maintenance.",
    category: "Palliative",
    cover_image_path:
      "https://images.unsplash.com/photo-1589998059171-988d887df646?w=300&h=400&fit=crop",
    pdf_file_path: "/books/equipments.pdf",
    rating: 4.8,
    published_year: 2026,
    language: "Malayalam",
  },
  // Add more books here...
];

const BookList = () => {
  const [selectedBook, setSelectedBook] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("All");
  const navigate = useNavigate();
  // Add this state at the top with your other useState declarations
  const [numPages, setNumPages] = useState(null);
  // Add this callback function
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };
  const handleLogout = () => {
    // Clear user data from localStorage
    localStorage.removeItem("user");
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("loginTime");
    // Navigate to login page
    navigate("/login");
  };

  const filteredBooks = sampleBooks.filter((book) => {
    const matchesSearch =
      book.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      book.author.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      filterCategory === "All" || book.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = [];

  const openBook = (book) => {
    setSelectedBook(book);
  };

  const closeBook = () => {
    setSelectedBook(null);
  };

  const StarRating = ({ rating }) => (
    <div className="flex items-center">
      {[...Array(5)].map((_, i) => (
        <span
          key={i}
          className={`text-xl ${i < Math.floor(rating) ? "text-yellow-400" : "text-gray-300"}`}
        >
          ★
        </span>
      ))}
      <span className="ml-2 text-sm text-gray-600">({rating})</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-base-200">
      {/* Navbar */}
      <div className="navbar bg-base-100 shadow-lg">
        <div className="flex-1 px-6">
          <h1 className="text-3xl font-bold text-primary">📚 e-Library</h1>
          <h1 className="text-xl">Palliative Care Manjeri</h1>
        </div>
        <div className="flex-none px-6">
          <button onClick={handleLogout} className="btn btn-outline btn-error">
            Logout
          </button>
        </div>
        <div className="flex-none px-6">
          <input
            type="text"
            placeholder="Search by title or author..."
            className="input input-bordered w-80 focus:outline-none"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Category Filters */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setFilterCategory(cat)}
              className={`btn btn-sm ${filterCategory === cat ? "btn-primary" : "btn-ghost"}`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Results Count */}
        {/* <p className="mb-6 text-gray-600">{filteredBooks.length} books found</p> */}

        {/* Book Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredBooks.map((book) => (
            <div
              key={book.id}
              className="card bg-base-100 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden"
            >
              <figure className="px-4 pt-4">
                <img
                  src={book.cover_image_path}
                  alt={book.title}
                  className="w-full h-64 object-cover rounded-xl shadow-md hover:scale-105 transition-transform duration-300"
                />
              </figure>

              <div className="card-body">
                <div className="badge badge-primary badge-outline mb-2">
                  {book.category}
                </div>
                <h2 className="card-title text-lg line-clamp-2">
                  {book.title}
                </h2>
                <p className="text-sm text-gray-600">{book.author}</p>
                <p className="text-xs text-gray-500">
                  {book.language}({book.published_year})
                </p>
                <p className="text-sm text-gray-700 line-clamp-3 mt-2">
                  {book.description}
                </p>

                <StarRating rating={book.rating} />

                <div className="card-actions mt-4">
                  <button
                    onClick={() => openBook(book)}
                    className="btn btn-primary btn-block"
                  >
                    📖 Read Now
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* PDF Reader Modal - Fullscreen Scrollable */}
      {selectedBook && (
        <div className="fixed inset-0 bg-black z-50 flex flex-col">
          {/* Header */}
          <div className="bg-base-100 p-4 sm:p-6 border-b flex items-center justify-between sticky top-0 shadow-md">
            <div className="min-w-0 flex-1">
              <h3 className="font-bold text-lg sm:text-xl truncate">
                {selectedBook.title}
              </h3>
              <p className="text-sm text-gray-600 truncate">
                {selectedBook.author}
              </p>
            </div>
            <button
              onClick={closeBook}
              className="btn btn-error btn-sm ml-4 flex-shrink-0"
            >
              ✕
            </button>
          </div>

          {/* PDF Viewer - Scrollable */}
          <div className="flex-1 overflow-y-auto bg-gray-900 p-2 sm:p-4 flex flex-col items-center">
            <Document
            onLoadSuccess={onDocumentLoadSuccess}
              file={selectedBook.pdf_file_path}
              loading={
                <div className="flex flex-col items-center justify-center py-20">
                  <span className="loading loading-spinner loading-lg text-primary"></span>
                  <p className="mt-4 text-lg text-white">Loading PDF...</p>
                </div>
              }
              error={
                <div className="text-red-500 text-center py-10">
                  Failed to load the PDF.
                  <br />
                  Please check the file URL.
                </div>
              }
            >
              {Array.from(new Array(numPages), (_, i) => (
                <Page
                  key={i}
                  pageNumber={i + 1}
                  width={Math.min(window.innerWidth - 16, 800)}
                  className="shadow-lg mb-4 bg-white"
                />
              )).filter((page) => page !== null)}
            </Document>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookList;
